import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';
import AssumeUserMenu from './AssumeUserMenu';
import { canAssumeUsers } from '../../utils/assumeUser';
import { useTheme } from '../../context/ThemeContext';
import { scrollAppToTop } from '../../utils/scrollAppToTop';
import { WFM_MODULES, WFM_MODULE_GROUPS, WFM_SYSTEM_TOOLS, resolveActiveModule, isLauncherModule, canOpenModule, canOpenLink, hasModuleSidebar, moduleDescription } from '../../config/wfmModules';
import { MOBILE_BREAKPOINT_PX, isMobileUiEnabled, getIsMobileViewport } from '../../utils/mobileUi';
import ModuleIcon from '../common/ModuleIcon';
import NavIcon from '../common/NavIcons';
import useScrollFade, { scrollFadeClass } from '../common/useScrollFade';
import useConfirmDialog from '../common/useConfirmDialog';
import GlobalSearch from '../GlobalSearch/GlobalSearch';
import NotificationCenter from './NotificationCenter';
import './SuiteNavbar.css';

const getBaseUrl = () => (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const MOBILE_UI_ENABLED = isMobileUiEnabled();
// Match the closing animations in SuiteNavbar.css (suite-switcher-out, suite-drawer-out).
const SWITCHER_CLOSE_MS = 140;
const MOBILE_DRAWER_CLOSE_MS = 180;

// A page name under its sidebar icon takes one word per row ("Employee" / "List"). An "&" stays with the word after it ("& Learning") and
// a word of one or two letters with the one after it when both are short ("TL Reports"), or they would sit alone on a row.
const NBSP = ' ';
const stackWords = (label) => String(label || '')
  .replace(/& /g, `&${NBSP}`)
  .replace(/(^| )(\w{1,2}) (?=\w{1,6}( |$))/g, `$1$2${NBSP}`);

const SuiteNavbar = () => {
  const { user, realUser, logout, assumed, assumeUser, stopAssuming } = useAuth();
  // First and last name, never the nickname and never the role: most people will have no nickname.
  const userLabel = user?.displayName || user?.username || '';
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [lightLogoFailed, setLightLogoFailed] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // The panel stays mounted for its closing animation, so it eases out instead of vanishing.
  // Every close path only flips switcherOpen; this follows it, SWITCHER_CLOSE_MS behind.
  const [switcherMounted, setSwitcherMounted] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => getIsMobileViewport(MOBILE_BREAKPOINT_PX));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Same close-then-unmount pattern as the switcher, for the drawer's slide out.
  const [mobileMenuMounted, setMobileMenuMounted] = useState(false);
  const headerRef = useRef(null);
  const switcherRef = useRef(null);
  const mobileUi = MOBILE_UI_ENABLED && isMobileViewport;

  const activeModule = useMemo(() => resolveActiveModule(pathname), [pathname]);
  const availableModules = useMemo(
    () => WFM_MODULES.filter((mod) => isLauncherModule(mod) && canOpenModule(user, mod)),
    [user]
  );
  const systemTools = useMemo(
    () => WFM_SYSTEM_TOOLS.filter((mod) => canOpenModule(user, mod)),
    [user]
  );
  const subLinks = useMemo(() => {
    if (!activeModule) return [];
    return (activeModule.subLinks || []).filter((link) => canOpenLink(user, link));
  }, [activeModule, user]);
  const showSubnav = hasModuleSidebar(subLinks);
  // On a computer the module's pages are a sidebar down the left of the page (an icon over its name, on a second row when it is long, as in Slack); on a phone
  // they stay the one row under the header that scrolls sideways.
  const showSidenav = showSubnav && !isMobileViewport;
  const showSubnavRow = showSubnav && isMobileViewport;
  // Prefer the longest matching path so /productive-hours does not stay
  // active on /productive-hours/reports (same for /schedule vs /schedule/settings).
  const activeSubTo = useMemo(() => {
    // A profile is reached from the Employee List, so that is the tab that stays lit on it.
    const here = pathname.startsWith('/agent-dossier/profile/') ? '/agent-dossier/employees' : pathname;
    const matches = subLinks.filter(
      (link) => here === link.to || here.startsWith(`${link.to}/`)
    );
    if (!matches.length) return null;
    return matches.reduce((best, link) => (link.to.length > best.to.length ? link : best)).to;
  }, [pathname, subLinks]);

  const subnavRef = useRef(null);
  const subnavFade = useScrollFade(subnavRef, `${showSubnav}|${activeSubTo}|${subLinks.length}`);

  // The page makes room for the sidebar (App.css, .has-suite-sidenav).
  useEffect(() => {
    if (!showSidenav) return undefined;
    document.body.classList.add('has-suite-sidenav');
    return () => document.body.classList.remove('has-suite-sidenav');
  }, [showSidenav]);

  useEffect(() => { setLightLogoFailed(false); }, [theme]);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsMobileViewport(getIsMobileViewport(MOBILE_BREAKPOINT_PX));
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
    setSwitcherOpen(false);
  }, [pathname]);

  // Log out asks first, so a stray click on the corner button does not end the session.
  const [askConfirm, confirmDialog] = useConfirmDialog();
  const handleLogout = async () => {
    const yes = await askConfirm({
      title: 'Log out?',
      message: 'You will need to sign in again to come back.',
      confirmLabel: 'Log out',
      closeLabel: 'Stay signed in'
    });
    if (!yes) return;
    logout();
    navigate('/login');
  };

  const canOpenOwnDossier = hasPermission(user, 'dossier.access');
  const goToOwnDossier = () => {
    setMobileMenuOpen(false);
    navigate('/agent-dossier?me=1');
  };

  const base = getBaseUrl();
  const logoSrc = theme === 'dark'
    ? `${base}/logo/logo-white.png`
    : (lightLogoFailed ? `${base}/logo/logo-original.png` : `${base}/logo/logo-light.png`);

  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const syncOffset = () => {
      document.documentElement.style.setProperty('--suite-navbar-offset', `${el.offsetHeight}px`);
    };
    syncOffset();
    const ro = new ResizeObserver(syncOffset);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--suite-navbar-offset');
    };
  }, [showSubnav, mobileUi, theme]);

  useEffect(() => {
    if (switcherOpen) {
      setSwitcherMounted(true);
      return undefined;
    }
    const timer = setTimeout(() => setSwitcherMounted(false), SWITCHER_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [switcherOpen]);

  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuMounted(true);
      return undefined;
    }
    const timer = setTimeout(() => setMobileMenuMounted(false), MOBILE_DRAWER_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!switcherOpen) return undefined;
    const onPointerDown = (event) => {
      const root = switcherRef.current;
      if (root && !root.contains(event.target)) setSwitcherOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSwitcherOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [switcherOpen]);

  // Gated on the REAL account: while viewing as someone, `user` is that person and would hide the only control that
  // ends the view.
  // The system Admin and Zendesk Administrator (the account Ivan signs in with); the server decides the same way.
  const canAssume = canAssumeUsers(realUser?.role);

  const onNavClick = () => {
    scrollAppToTop();
    setMobileMenuOpen(false);
    setSwitcherOpen(false);
  };

  // A page that is not a module (the notifications list, the full search results) says what it is; the home page keeps "Navigation".
  const navLabel = activeModule
    ? activeModule.label
    : (pathname.startsWith('/notifications') ? 'Notifications' : (pathname.startsWith('/search') ? 'Search' : 'Navigation'));

  return (
    // Raised above the overlay for as long as the panel is on screen, closing animation included.
    // Dropping it on switcherOpen alone sank the fading panel behind the overlay for its last
    // 140ms, which read as a flicker.
    <header
      ref={headerRef}
      className={`suite-navbar${switcherOpen || switcherMounted ? ' is-switcher-open' : ''}`}
    >
      <div className="suite-navbar-row suite-navbar-row--primary">
        <div className="suite-navbar-left">
          <div className="suite-switcher" ref={switcherRef}>
            <button
              type="button"
              className={`suite-nav-btn ${switcherOpen ? 'is-open' : ''}`}
              onClick={() => setSwitcherOpen((v) => !v)}
              aria-expanded={switcherOpen}
              aria-haspopup="menu"
            >
              <span className="suite-nav-btn-icon" aria-hidden="true">{'\u2630'}</span>
              <span className="suite-nav-btn-text">{navLabel}</span>
              <span className="suite-nav-btn-caret" aria-hidden="true">{'\u25BE'}</span>
            </button>
            {switcherMounted && typeof document !== 'undefined'
              ? createPortal(
                <div
                  className={`suite-switcher-overlay${switcherOpen ? '' : ' is-closing'}`}
                  onClick={() => setSwitcherOpen(false)}
                  aria-hidden="true"
                />,
                document.body
              )
              : null}
            {switcherMounted && (
              <div
                className={`suite-switcher-panel${switcherOpen ? '' : ' is-closing'}`}
                role="menu"
                aria-hidden={switcherOpen ? undefined : 'true'}
              >
                  <Link to="/home" className="suite-nav-home-link" onClick={onNavClick}>
                    <span className="suite-nav-home-title">E-OPS</span>
                    <span className="suite-nav-home-sub">Home page</span>
                  </Link>
                  {WFM_MODULE_GROUPS.map((group) => {
                    const mods = availableModules.filter((m) => m.group === group.id);
                    if (!mods.length) return null;
                    return (
                      <div key={group.id} className="suite-nav-group">
                        <div className="suite-nav-group-label">{group.label}</div>
                        {mods.map((mod) => (
                          <Link
                            key={mod.id}
                            to={mod.path}
                            className={`suite-nav-app ${activeModule?.id === mod.id ? 'is-active' : ''}`}
                            onClick={onNavClick}
                          >
                            <ModuleIcon mod={mod} className="suite-nav-app-icon" />
                            <span className="suite-nav-app-body">
                              <span className="suite-nav-app-title">
                                {mod.label}
                                {mod.comingSoon ? <em className="suite-nav-preview">Preview</em> : null}
                              </span>
                              <span className="suite-nav-app-desc">{moduleDescription(user, mod)}</span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
          {!mobileUi && systemTools.length > 0 && (
            <nav className="suite-system-links" aria-label="System">
              {systemTools.map((tool) => (
                <Link
                  key={tool.id}
                  to={tool.path}
                  className={`suite-system-link ${activeModule?.id === tool.id ? 'is-active' : ''}`}
                  onClick={onNavClick}
                  title={tool.description}
                >
                  {tool.label}
                </Link>
              ))}
            </nav>
          )}
        </div>

        <Link to="/home" className="suite-navbar-brand" onClick={onNavClick}>
          {/* The wordmark itself reads "GMS E-OPS", so no separate title text sits beside it. */}
          <img
            src={logoSrc}
            alt="GMS E-OPS"
            className={`suite-navbar-logo ${theme === 'dark' ? 'suite-navbar-logo-dark' : 'suite-navbar-logo-light'}`}
            onError={() => { if (theme === 'light') setLightLogoFailed(true); }}
          />
        </Link>

        <div className="suite-navbar-right">
          <GlobalSearch compact={mobileUi} />
          {mobileUi ? (
            <button
              type="button"
              className="suite-icon-btn"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-expanded={mobileMenuOpen}
              aria-label="Open menu"
            >
              {'\u22EF'}
            </button>
          ) : (
            <>
              {/* The whole chip is the target, not just the name, so a click anywhere on it opens the profile. */}
              {canOpenOwnDossier ? (
                <button
                  type="button"
                  className="suite-user-chip suite-user-chip--link"
                  onClick={goToOwnDossier}
                  title="Open your Employee Dossier"
                >
                  <span className="suite-navbar-username">{userLabel}</span>
                </button>
              ) : (
                <div className="suite-user-chip">
                  <span className="suite-navbar-username">{userLabel}</span>
                </div>
              )}
              {canAssume && <AssumeUserMenu assumed={assumed} realUserId={realUser?.id} onAssume={assumeUser} />}
              <NotificationCenter />
              <button
                type="button"
                onClick={toggleTheme}
                className="suite-icon-btn"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? '\u2600' : '\u263E'}
              </button>
              <button type="button" onClick={handleLogout} className="suite-logout-btn">
                Log out
              </button>
            </>
          )}
        </div>
      </div>

      {canAssume && assumed && (
        <div className="suite-navbar-row suite-navbar-row--preview">
          <span className="suite-preview-banner-text">
            Viewing the app as <strong>{assumed.name}</strong>{assumed.roleLabel ? ` (${assumed.roleLabel})` : ''}. You see exactly what
            they see, read-only: nothing can be changed in their name.
          </span>
          <button type="button" className="suite-preview-banner-exit" onClick={stopAssuming}>
            Exit view
          </button>
        </div>
      )}

      {showSubnavRow && (
        <div className="suite-navbar-row suite-navbar-row--secondary">
          <nav ref={subnavRef} className={`suite-subnav ${scrollFadeClass(subnavFade)}`.trim()} aria-label="Module navigation">
            {subLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                state={activeModule?.id === 'dossier' ? { closeDossierProfile: true } : undefined}
                className={`suite-subnav-link ${activeSubTo === link.to ? 'is-active' : ''}`}
                onClick={(e) => {
                  onNavClick();
                  if (activeModule?.id !== 'dossier') return;
                  // Same-path Employees click must still leave an open profile.
                  if (pathname === link.to) {
                    e.preventDefault();
                    navigate(link.to, { state: { closeDossierProfile: true } });
                  }
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}

      {/* The module's pages, down the left of the page. Portaled to <body> for the same reason as the overlays below: inside the header
          `backdrop-filter` makes the header the containing block for position: fixed. */}
      {showSidenav && typeof document !== 'undefined' && createPortal(
        <nav className="suite-sidenav" aria-label="Module navigation">
          {subLinks.map((link) => {
            const base = 'suite-sidenav-link';
            return (
              <Link
                key={link.to}
                to={link.to}
                state={activeModule?.id === 'dossier' ? { closeDossierProfile: true } : undefined}
                className={`${base} ${activeSubTo === link.to ? 'is-active' : ''}`}
                title={link.label}
                aria-label={link.label}
                aria-current={activeSubTo === link.to ? 'page' : undefined}
                onClick={(e) => {
                  onNavClick();
                  if (activeModule?.id !== 'dossier') return;
                  // Same-path Employees click must still leave an open profile.
                  if (pathname === link.to) {
                    e.preventDefault();
                    navigate(link.to, { state: { closeDossierProfile: true } });
                  }
                }}
              >
                <NavIcon name={link.glyph} label={link.label} />
                <span>{stackWords(link.label)}</span>
              </Link>
            );
          })}
        </nav>,
        document.body
      )}

      {/* Portaled to <body>, like the switcher overlay. Inside the header, `backdrop-filter`
          makes the header the containing block for position: fixed, so the overlay shrank to the
          header's 63px: it dimmed the top of the drawer and left the page unclickable-through. */}
      {mobileUi && mobileMenuMounted && typeof document !== 'undefined' && createPortal(
        <>
          <div
            className={`suite-mobile-overlay${mobileMenuOpen ? '' : ' is-closing'}`}
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            className={`suite-mobile-drawer${mobileMenuOpen ? '' : ' is-closing'}`}
            role="dialog"
            aria-label="Menu"
            aria-hidden={mobileMenuOpen ? undefined : 'true'}
          >
            {canOpenOwnDossier ? (
              <button
                type="button"
                className="suite-mobile-user suite-user-chip--link"
                onClick={goToOwnDossier}
                title="Open your Employee Dossier"
              >
                <span className="suite-navbar-username">{userLabel}</span>
              </button>
            ) : (
              <div className="suite-mobile-user">
                <span>{userLabel}</span>
              </div>
            )}
            <Link to="/home" className="suite-mobile-link" onClick={onNavClick}>E-OPS</Link>
            {availableModules.map((mod) => (
              <Link key={mod.id} to={mod.path} className="suite-mobile-link" onClick={onNavClick}>
                <ModuleIcon mod={mod} className="suite-mobile-link-icon" />
                {mod.label}
              </Link>
            ))}
            {systemTools.length > 0 && (
              <div className="suite-mobile-system">
                <div className="suite-mobile-system-label">System</div>
                {systemTools.map((tool) => (
                  <Link key={tool.id} to={tool.path} className="suite-mobile-link" onClick={onNavClick}>
                    <ModuleIcon mod={tool} className="suite-mobile-link-icon" />
                    {tool.label}
                  </Link>
                ))}
              </div>
            )}
            <div className="suite-mobile-actions">
              {canAssume && <AssumeUserMenu assumed={assumed} realUserId={realUser?.id} onAssume={assumeUser} />}
              <NotificationCenter />
              <button type="button" className="suite-icon-btn" onClick={toggleTheme}>
                {theme === 'dark' ? '\u2600' : '\u263E'}
              </button>
              <button type="button" className="suite-logout-btn" onClick={handleLogout}>
                Log out
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
      {/* In the body, not the header: a fixed modal inside the header can be clipped by the header's own box. */}
      {createPortal(confirmDialog, document.body)}
    </header>
  );
};

export default SuiteNavbar;
